// THE RECENT FILES PAGE (Figma 40014119:480632 "Add from Busy · nothing selected", Pouria 2026-10-14:
// "I need this one when we click on the recent files").
//
// The box's + menu → "Recent saved files ›" used to swap the attach pane to a small ticked list
// (busy-my-web#408). It now opens a PAGE in the middle of the screen — a grid of saved-file cards —
// while the box below turns into the "Briefing Recent files" filter row, and "← Back" in the gutter
// hands the picks to the Work-board's "ready to attach" group.
//
// This module is everything about that page that is not drawing: the card a document becomes (its
// type glyph and colour, its name split for the frame's truncation, where it came from, its time
// line), the five filter pills and their counts, the name filter, and the ⋮ menu's items. No store,
// no fetch, no component — the documents are the BFF's `documents` page, read as they arrive.
//
// ── WHAT THE PAYLOAD CAN AND CANNOT SAY (busy-main-bff DocumentResponse, 2026-10-14) ─────────────
//   • origin   — `provider` (busy-document-srv DocumentProviderEnum: 1 GmailMessage, 2 GoogleDrive,
//                3 RemoteUrl). A file uploaded from the computer is stored in the user's private
//                Drive area with provider GoogleDrive, so "Uploaded" and "Google drive" cannot be
//                told apart: provider 2 reads "Google drive".
//   • source   — `timelineHeadLine` (the subject of the conversation the file came in on), then
//                `relatedEmail` (who it is with). There is no Ongoing / task NAME on a document, so
//                the frame's "Prada order" (an Ongoing) can only be a thread subject here.
//   • time     — `createdOn` (when the row was made: the upload, or the mail sync). There is no
//                "opened" timestamp, so the frame's "Opened today, 14:20" is not drawn; the line
//                says "Uploaded …" for a Drive file and "Saved …" for everything else.

import { fileExtension } from '@/lib/briefings/entityFiles'
import { isImageAttachment } from '@/lib/globalTimelineFeed'
import { parseAttachmentClass, ATTACHMENT_CLASS } from '@/lib/attachmentsBriefing'
import { parseUtc } from '@/lib/dueStatus'
import { recentRow, savedAt, stagedRecentFile, matchRecentRows } from '@/lib/recentSavedFiles'

/** How many documents one read of the page asks for (the small list asked for 25). */
export const PAGE_SIZE = 60

/** The page's own words. */
export const RECENT_PAGE_TEXT = {
  title: 'Recent files',
  subtitle: 'Select files for Work-board',
  briefing: 'Recent files',
  placeholder: 'Search or ask busy...',
  mobilePlaceholder: 'Search or ask Busy...',
  cancel: 'Cancel',
  search: 'Search',
  clear: 'Clear search',
  addToBoard: 'Add to Work-board',
  board: 'Work-board',
  boardEmpty: 'No files yet',
  selectedItems: 'Selected items',
  loading: 'Loading your saved files…',
  empty: 'Nothing saved yet.',
  noMatch: 'No saved files match that.',
  failed: 'Could not load your saved files.',
  // The no-results state — frame 46's wording (busy-my-web#633, Pouria 2026-10-15: "the wording is
  // like the second one"), not frames 47 / m43's "No files match …" / "Try another word…".
  noResultsSubtitle: "Try different keywords, a filename, or a sender's name.",
}

/** The no-results title for a searched query — `No files found for “invoice 2023”` (frame 46, #633). */
export function noResultsTitle(query) {
  return `No files found for \u201c${String(query || '').trim().toLowerCase()}\u201d`
}

/** The list view's column heads (frame 49), in order. */
export const LIST_COLUMNS = ['Name', 'Size', 'Kind', 'Related email', 'Status']

// ── File types ───────────────────────────────────────────────────────────────────────────────────
// The head's 16px silhouette and the preview tile's label, per kind of file. The colours are read
// off the frame (40014119:480632) — the app had no per-type palette before this page; the PDF red is
// the one the Work-board's ready chip already uses (lib/workboardAttach `readyFileIcon`).
const TYPES = {
  image: { label: 'IMG', color: '#6b7280' },
  pdf: { label: 'PDF', color: '#ff0000' },
  doc: { label: 'DOC', color: '#2563eb' },
  sheet: { label: 'XLS', color: '#15803d' },
  slide: { label: 'PPT', color: '#c2410c' },
  design: { label: 'FIG', color: '#7838e4' },
  audio: { label: 'MP3', color: '#be185d' },
  video: { label: 'MP4', color: '#4f46e5' },
  archive: { label: 'ZIP', color: '#b45309' },
  text: { label: 'TXT', color: '#4b5563' },
  file: { label: 'FILE', color: '#6b7280' },
}

const EXT_TYPES = {
  pdf: 'pdf',
  doc: 'doc', docx: 'doc', odt: 'doc', rtf: 'doc', pages: 'doc',
  xls: 'sheet', xlsx: 'sheet', xlsm: 'sheet', ods: 'sheet', csv: 'sheet', tsv: 'sheet', numbers: 'sheet',
  ppt: 'slide', pptx: 'slide', odp: 'slide', key: 'slide',
  fig: 'design', sketch: 'design', psd: 'design', ai: 'design', xd: 'design',
  mp3: 'audio', wav: 'audio', m4a: 'audio', ogg: 'audio', oga: 'audio', aac: 'audio', flac: 'audio',
  mp4: 'video', mov: 'video', webm: 'video', avi: 'video', mkv: 'video', m4v: 'video',
  zip: 'archive', rar: 'archive', '7z': 'archive', tar: 'archive', gz: 'archive',
  txt: 'text', md: 'text', log: 'text', json: 'text',
}

/** The bare MIME type, lower-cased. */
const mimeOf = (doc) => String(doc?.contentType || '').split(';')[0].trim().toLowerCase()

/** A document's file name, whichever field carries it. */
const nameOf = (doc) => String(doc?.filename || doc?.fileName || doc?.name || '').trim()

/**
 * What kind of file a document is, for its glyph, colour and tile label.
 *
 * @param {{ filename?: string, contentType?: string }} doc
 * @returns {{ key: string, label: string, color: string }}
 */
export function fileTypeOf(doc) {
  const ext = fileExtension(nameOf(doc))
  const mime = mimeOf(doc)
  let key = EXT_TYPES[ext] || ''
  if (!key && isImageAttachment({ contentType: mime, extension: ext })) key = 'image'
  if (!key && mime === 'application/pdf') key = 'pdf'
  if (!key && mime.startsWith('audio/')) key = 'audio'
  if (!key && mime.startsWith('video/')) key = 'video'
  if (!key && mime.startsWith('text/')) key = 'text'
  key = key || 'file'
  return { key, ...TYPES[key] }
}

// ── Filter pills ─────────────────────────────────────────────────────────────────────────────────

/** The box's pills, in the frame's order. */
export const RECENT_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'images', label: 'Images' },
  { key: 'docs', label: 'Docs' },
  { key: 'pdfs', label: 'PDFs' },
  { key: 'uncategorised', label: 'Uncategorised' },
]

const FILTER_KEYS = RECENT_FILTERS.map(f => f.key)

/** A pill key, or 'all' for anything that is not one. */
export function parseRecentFilter(key) {
  const k = String(key || '').toLowerCase()
  return FILTER_KEYS.includes(k) ? k : 'all'
}

/**
 * Which pill a document sits under. The SHAPE of the file decides: busy-document-srv's own class
 * wins where it is a shape (pdf / image / document / spreadsheet / presentation), and its word
 * classes (invoice, receipt — words found inside the file) fall back to the shape, because an
 * invoice PDF is still a PDF to someone looking for PDFs.
 *
 * @returns {'images'|'docs'|'pdfs'|'uncategorised'}
 */
export function filterClassOf(doc) {
  const stated = parseAttachmentClass(doc?.class)
  const shape = stated === ATTACHMENT_CLASS.pdf || stated === ATTACHMENT_CLASS.image
    || stated === ATTACHMENT_CLASS.document || stated === ATTACHMENT_CLASS.spreadsheet
    || stated === ATTACHMENT_CLASS.presentation
    ? stated
    : null
  const type = fileTypeOf(doc).key
  const cls = shape || (type === 'pdf' ? ATTACHMENT_CLASS.pdf
    : type === 'image' ? ATTACHMENT_CLASS.image
      : ['doc', 'sheet', 'slide', 'text'].includes(type) ? ATTACHMENT_CLASS.document
        : null)
  if (cls === ATTACHMENT_CLASS.image) return 'images'
  if (cls === ATTACHMENT_CLASS.pdf) return 'pdfs'
  if (cls) return 'docs'
  return 'uncategorised'
}

// ── Where it came from ───────────────────────────────────────────────────────────────────────────

const PROVIDERS = {
  1: 'Gmail', gmailmessage: 'Gmail',
  2: 'Google drive', googledrive: 'Google drive',
  3: 'Link', remoteurl: 'Link',
}

/** "Gmail" / "Google drive" / "Link", from the document's `provider`; '' when it does not say. */
export function originOf(doc) {
  const p = doc?.provider
  if (p === null || p === undefined || p === '') return ''
  return PROVIDERS[String(p).toLowerCase()] || ''
}

/**
 * The thing the file came in on: the conversation's subject (`timelineHeadLine`), else who it is
 * with (`relatedEmail`), with the email glyph when the file is a mail part. `timelineId` is what
 * "Show in …" opens. A file on no thread has no source — the line is then the origin alone.
 *
 * @returns {{ name: string, icon: string, timelineId: string }}
 */
export function sourceOf(doc) {
  const name = String(doc?.timelineHeadLine || '').trim() || String(doc?.relatedEmail || '').trim()
  const timelineId = String(doc?.timelineId || '')
  return {
    name,
    icon: originOf(doc) === 'Gmail' ? 'sms' : 'attachments',
    timelineId,
  }
}

// ── The time line ────────────────────────────────────────────────────────────────────────────────

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad = (n) => String(n).padStart(2, '0')
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/**
 * "today, 14:20" / "yesterday, 18:40" / "6 Oct, 16:30" / "6 Oct 2025, 16:30" — local time, the
 * frame's wording. '' for a missing or unreadable stamp.
 */
export function whenText(stamp, now = Date.now()) {
  if (stamp === null || stamp === undefined || stamp === '') return ''
  const d = stamp instanceof Date ? stamp : parseUtc(stamp)
  if (Number.isNaN(d.getTime())) return ''
  const today = new Date(now)
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`
  if (sameDay(d, today)) return `today, ${time}`
  if (sameDay(d, yesterday)) return `yesterday, ${time}`
  const year = d.getFullYear() === today.getFullYear() ? '' : ` ${d.getFullYear()}`
  return `${d.getDate()} ${MONTHS[d.getMonth()]}${year}, ${time}`
}

/** The card's second footer line — "Uploaded 5 Oct, 20:15" for a Drive file, "Saved …" otherwise. */
export function timeLineOf(doc, now = Date.now()) {
  const when = whenText(doc?.createdOn || doc?.createdDate || null, now)
  if (!when) return ''
  return `${originOf(doc) === 'Google drive' ? 'Uploaded' : 'Saved'} ${when}`
}

// ── The list view's columns (frame 49: Name / Size / Kind / Related email / Status) ──────────────

/** "10 KB" / "3.2 MB" / "12 MB" from the document's `octets` — '' when it does not say. */
export function sizeText(octets) {
  const n = Number(octets)
  if (!Number.isFinite(n) || n <= 0) return ''
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB']
  let v = n / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1 }
  // Kilobytes whole; above that one decimal under ten ("3.2 MB"), whole from ten up ("12 MB").
  const shown = i === 0 || v >= 10 ? String(Math.round(v)) : String(Math.round(v * 10) / 10)
  return `${shown} ${units[i]}`
}

// The Kind column's words, by extension (the frame's own: "JPEG Image", "PDF Document", …).
const KIND_BY_EXT = {
  jpg: 'JPEG Image', jpeg: 'JPEG Image',
  pdf: 'PDF Document',
  doc: 'Word Document', docx: 'Word Document',
  ppt: 'PowerPoint', pptx: 'PowerPoint',
  xls: 'Excel Spreadsheet', xlsx: 'Excel Spreadsheet',
  fig: 'Figma File',
  mp3: 'MP3 Audio',
  mp4: 'MPEG-4 Video', m4v: 'MPEG-4 Video',
  txt: 'Text File',
  csv: 'CSV Document',
  zip: 'ZIP Archive',
}
const KIND_BY_TYPE = {
  image: 'Image', pdf: 'PDF Document', doc: 'Document', sheet: 'Spreadsheet', slide: 'Presentation',
  design: 'Design File', audio: 'Audio', video: 'Video', archive: 'Archive', text: 'Text File',
}

/** The Kind column: "JPEG Image", "PNG Image", "MP3 Audio", "File"… */
export function kindOf(doc) {
  const ext = fileExtension(nameOf(doc))
  if (KIND_BY_EXT[ext]) return KIND_BY_EXT[ext]
  const type = fileTypeOf(doc).key
  const word = KIND_BY_TYPE[type]
  if (!word) return ext ? `${ext.toUpperCase()} File` : 'File'
  // "PNG Image", "WAV Audio" — the extension names the flavour when the type word is generic.
  return ext && ['image', 'audio', 'video', 'archive'].includes(type) ? `${ext.toUpperCase()} ${word}` : word
}

/**
 * The Status column's time, 12-hour as the frame writes it: "today, 12:45 pm" / "yesterday, 9:12 am"
 * / "Oct 8, 12:04 pm" / "Oct 8 2025, 12:04 pm". '' for a missing or unreadable stamp.
 */
export function whenText12(stamp, now = Date.now()) {
  if (stamp === null || stamp === undefined || stamp === '') return ''
  const d = stamp instanceof Date ? stamp : parseUtc(stamp)
  if (Number.isNaN(d.getTime())) return ''
  const today = new Date(now)
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  const h = d.getHours()
  const time = `${h % 12 || 12}:${pad(d.getMinutes())} ${h < 12 ? 'am' : 'pm'}`
  if (sameDay(d, today)) return `today, ${time}`
  if (sameDay(d, yesterday)) return `yesterday, ${time}`
  const year = d.getFullYear() === today.getFullYear() ? '' : ` ${d.getFullYear()}`
  return `${MONTHS[d.getMonth()]} ${d.getDate()}${year}, ${time}`
}

// An `updateOn` within this of `createdOn` is the row being written, not the file being edited.
const EDIT_GRACE_MS = 60 * 1000

/**
 * The Status column (frame 49 writes "Opened …" / "Edited …" / "Uploaded …"). There is no "opened"
 * stamp on a document, so: "Edited <when>" from `updateOn` when the file changed after it was saved,
 * else the card's own "Uploaded …" / "Saved …" from `createdOn` — 12-hour, as the table writes it.
 */
export function statusLineOf(doc, now = Date.now()) {
  const created = doc?.createdOn || doc?.createdDate || null
  const updated = doc?.updateOn || doc?.updatedOn || null
  if (updated) {
    const u = parseUtc(updated)
    const c = created ? parseUtc(created) : null
    if (!Number.isNaN(u.getTime()) && (!c || Number.isNaN(c.getTime()) || u.getTime() - c.getTime() > EDIT_GRACE_MS)) {
      return `Edited ${whenText12(u, now)}`
    }
  }
  const when = whenText12(created, now)
  if (!when) return ''
  return `${originOf(doc) === 'Google drive' ? 'Uploaded' : 'Saved'} ${when}`
}

// ── The card ─────────────────────────────────────────────────────────────────────────────────────

/** The name split for the frame's truncation: the stem is cut (~102px), the extension never is. */
export function nameParts(name) {
  const full = String(name || '')
  const dot = full.lastIndexOf('.')
  if (dot <= 0 || dot === full.length - 1) return { stem: full, ext: '' }
  return { stem: full.slice(0, dot), ext: full.slice(dot) }
}

/**
 * One saved-file card: everything the grid, the list, the ⋮ menu and the box need, with the row
 * lib/recentSavedFiles makes (`row`) riding along so a pick is staged exactly as before.
 */
export function recentFileCard(doc, now = Date.now()) {
  const row = recentRow(doc)
  const type = fileTypeOf(doc)
  const source = sourceOf(doc)
  const origin = originOf(doc)
  return {
    id: row.id,
    name: row.name,
    ...nameParts(row.name),
    type,
    filter: filterClassOf(doc),
    isImage: type.key === 'image',
    hasThumbnail: doc?.hasThumbnail === true ? true : doc?.hasThumbnail === false ? false : undefined,
    contentType: row.contentType,
    source,
    origin,
    timeLine: timeLineOf(doc, now),
    size: sizeText(doc?.octets),
    kind: kindOf(doc),
    status: statusLineOf(doc, now),
    savedAt: row.savedAt,
    row,
  }
}

/** Every document as a card, newest first (ties keep the server's order); id-less rows dropped. */
export function recentFileCards(docs, now = Date.now()) {
  return (Array.isArray(docs) ? docs : [])
    .map((d, index) => ({ card: recentFileCard(d, now), at: savedAt(d), index }))
    .filter(({ card }) => !!card.id)
    .sort((a, b) => {
      const at = a.at ? a.at.getTime() : null
      const bt = b.at ? b.at.getTime() : null
      if (at === bt) return a.index - b.index
      if (at === null) return 1
      if (bt === null) return -1
      return bt - at
    })
    .map(({ card }) => card)
}

/** How many cards sit under each pill. */
export function filterCounts(cards) {
  const counts = { all: 0, images: 0, docs: 0, pdfs: 0, uncategorised: 0 }
  for (const c of cards || []) {
    if (!c) continue
    counts.all += 1
    counts[c.filter] = (counts[c.filter] || 0) + 1
  }
  return counts
}

/**
 * The pill counts from the server's whole-set totals (`documents/briefing`, busy-main-bff
 * DocumentClassCounts) — the frame counts every saved file ("All 56"), not the first page. Docs are
 * doc-srv's documents + spreadsheets + presentations; what no pill names (invoices, receipts — word
 * classes — and files with no class) is Uncategorised. Null when there is no usable answer.
 */
export function serverFilterCounts(body) {
  if (!body || typeof body !== 'object') return null
  const n = (k) => Math.max(0, Number(body[k]) || 0)
  const all = n('total')
  if (!all) return null
  const images = n('images')
  const pdfs = n('pdfs')
  const docs = n('documents') + n('spreadsheets') + n('presentations')
  return { all, images, docs, pdfs, uncategorised: Math.max(0, all - images - pdfs - docs) }
}

/**
 * The pill row: every pill, always (the frame draws all five), with its count and the active one.
 * `totals` (serverFilterCounts) wins while more pages are still to come; once every file is loaded
 * the loaded cards are the exact count.
 */
export function recentFilterPills(cards, active = 'all', totals = null) {
  const counts = totals || filterCounts(cards)
  const on = parseRecentFilter(active)
  return RECENT_FILTERS.map(f => ({ ...f, count: counts[f.key] || 0, active: f.key === on }))
}

/**
 * The name filter typed into the box. Words of three letters or more go through the same match
 * "Find related files" uses (`matchRecentRows`); a shorter query — someone still typing "pr" —
 * narrows by the letters themselves, so the grid answers from the first keystroke.
 */
export function matchCardsByName(cards, query) {
  const list = Array.isArray(cards) ? cards : []
  const q = String(query || '').trim().toLowerCase()
  if (!q) return list
  const hasWord = q.split(/[^a-z0-9]+/i).some(w => w.length >= 3)
  if (hasWord) return matchRecentRows(list, q)
  return list.filter(c => String(c?.name || '').toLowerCase().includes(q))
}

/** The grid: the pill in force, then the name filter. */
export function visibleCards(cards, { filter = 'all', query = '' } = {}) {
  const on = parseRecentFilter(filter)
  const byClass = on === 'all' ? (cards || []) : (cards || []).filter(c => c && c.filter === on)
  return matchCardsByName(byClass, query)
}

/** The grid's empty line, when there is one to say. */
export function emptyLine({ loading = false, failed = false, total = 0, shown = 0 } = {}) {
  if (loading || shown) return ''
  if (failed) return RECENT_PAGE_TEXT.failed
  return total ? RECENT_PAGE_TEXT.noMatch : RECENT_PAGE_TEXT.empty
}

/** "Search" is muted until something has been typed that has not been searched yet. */
export function searchMuted(query, applied = '') {
  const q = String(query || '').trim()
  return !q || q === String(applied || '').trim()
}

/**
 * The box's primary button (frames 40–50, m40–m47). Text typed and not yet searched → "Search"
 * (#3741f3); else picks → "Add to Work-board" (#3741f3); else a muted "Search" — on a phone too
 * since the updated mobile section (busy-my-web#636, Figma 40014298:306603 "nothing selected 3",
 * "list view", "disable category"), which supersedes m40/m46's muted "Add to Work-board".
 *
 * @returns {{ key: 'search'|'add', label: string, enabled: boolean }}
 */
export function primaryAction({ query = '', applied = '', picks = 0 } = {}) {
  if (!searchMuted(query, applied)) return { key: 'search', label: RECENT_PAGE_TEXT.search, enabled: true }
  if (picks > 0) return { key: 'add', label: RECENT_PAGE_TEXT.addToBoard, enabled: true }
  return { key: 'search', label: RECENT_PAGE_TEXT.search, enabled: false }
}

/**
 * Does the box show the filter pills (rather than the selection summary)? Nothing picked: always.
 * With picks the summary takes their place — on a phone too since busy-my-web#636 (frames "files
 * selected 6", "search open 9–12", "list view · selected") — except while the grid is the no-results
 * state, where the frames keep the pills so another category can be tried ("search open 8 / 13").
 */
export function showsFilterPills({ picks = 0, noResults = false } = {}) {
  return !picks || !!noResults
}

/**
 * A searched query that found nothing — the page's no-results state. A pill with nothing under it, or
 * a user with no files at all, keeps the plain empty line instead.
 */
export function isNoResults({ loading = false, shown = 0, total = 0, applied = '' } = {}) {
  return !loading && !shown && total > 0 && !!String(applied || '').trim()
}

// The summary's words per kind of file, singular / plural (frame 41: "1 image · 1 PDF · 1 audio").
const KIND_WORDS = {
  image: ['image', 'images'],
  pdf: ['PDF', 'PDFs'],
  doc: ['doc', 'docs'],
  sheet: ['spreadsheet', 'spreadsheets'],
  slide: ['presentation', 'presentations'],
  design: ['design file', 'design files'],
  audio: ['audio', 'audio'],
  video: ['video', 'videos'],
  archive: ['archive', 'archives'],
  text: ['text file', 'text files'],
  file: ['file', 'files'],
}

/**
 * The box's selection summary (frame 41): "3 files selected" over "1 image · 1 PDF · 1 audio", the
 * kinds in the order they were first picked. Null with nothing picked.
 */
export function selectionSummary(picked) {
  const list = (picked || []).filter(Boolean)
  if (!list.length) return null
  const counts = new Map()
  for (const c of list) {
    const key = KIND_WORDS[c.type?.key] ? c.type.key : 'file'
    counts.set(key, (counts.get(key) || 0) + 1)
  }
  const kinds = [...counts].map(([key, n]) => `${n} ${KIND_WORDS[key][n === 1 ? 0 : 1]}`).join(' · ')
  return { title: `${list.length} file${list.length === 1 ? '' : 's'} selected`, kinds }
}

// ── The ⋮ menu ───────────────────────────────────────────────────────────────────────────────────

/**
 * The card's menu: View, Select (Deselect once picked), a hairline, "Show in <source>" when the file
 * came in on a conversation, and Download.
 */
export function cardMenuItems(card, { selected = false } = {}) {
  const items = [
    { key: 'view', label: 'View', icon: 'eye' },
    { key: 'select', label: selected ? 'Deselect' : 'Select', icon: 'tick-square' },
  ]
  const tail = []
  if (card?.source?.name && card?.source?.timelineId) {
    tail.push({ key: 'show', label: `Show in ${card.source.name}`, icon: 'story' })
  }
  tail.push({ key: 'download', label: 'Download', icon: 'import' })
  return [...items, { key: 'divider', divider: true }, ...tail]
}

/** A card as the stored-document helpers in lib/attachmentOpen want it. */
export function openableFile(card) {
  return {
    id: String(card?.id || ''),
    name: card?.name || 'Attachment',
    contentType: card?.contentType || '',
    isImage: !!card?.isImage,
  }
}

// ── Back: what lands on the Work-board ───────────────────────────────────────────────────────────

/**
 * The picks as the box stages files (lib/recentSavedFiles `stagedRecentFile`), in the order they
 * were picked, leaving out any the board already holds — Back twice over the same file must not put
 * it on the board twice.
 *
 * @param {object[]} cards       every loaded card
 * @param {string[]} selectedIds the picked card ids, in pick order
 * @param {object[]} boardFiles  the box's staged files (`quickFiles`)
 */
export function stagedPicks(cards, selectedIds, boardFiles = []) {
  const byId = new Map((cards || []).filter(Boolean).map(c => [String(c.id), c]))
  const held = new Set((boardFiles || []).filter(Boolean).map(f => f.tempId))
  const out = []
  for (const id of selectedIds || []) {
    const card = byId.get(String(id))
    if (!card) continue
    const staged = stagedRecentFile(card.row)
    if (held.has(staged.tempId)) continue
    held.add(staged.tempId)
    out.push(staged)
  }
  return out
}

/** The picked cards, in pick order — the box's Work-board column while the page is up. */
export function pickedCards(cards, selectedIds) {
  const byId = new Map((cards || []).filter(Boolean).map(c => [String(c.id), c]))
  return (selectedIds || []).map(id => byId.get(String(id))).filter(Boolean)
}

/** Toggle one id in a pick list, keeping pick order. */
export function togglePick(selectedIds, id) {
  const key = String(id || '')
  if (!key) return [...(selectedIds || [])]
  const list = (selectedIds || []).map(String)
  return list.includes(key) ? list.filter(x => x !== key) : [...list, key]
}

// ── THE WAY BACK FROM A FILE (busy-my-web#644, Fatemeh 2026-10-16) ──────────────────────────────
// A file VIEWED from the page puts the page away (frames 60–63) and opens its briefing; that
// briefing's Close comes back HERE, to the page as it was left, rather than to the card the page
// was opened over. These two are that hand-off: what the page was showing, and the store patch
// that shows it again.

/**
 * What the page is showing, as plain values — kept by the file briefing (`fileBriefing/openedFrom`)
 * so its Close can reopen the very same page. Nothing about the documents themselves: those are
 * read afresh, as on any open.
 *
 * @param {object} s the recentFiles store state
 */
export function recentPageSnapshot(s) {
  const st = s || {}
  return {
    scope: st.scope || null,
    query: String(st.query || ''),
    applied: String(st.applied || ''),
    filter: parseRecentFilter(st.filter),
    view: st.view === 'list' ? 'list' : 'grid',
    selected: (Array.isArray(st.selected) ? st.selected : []).map(String).filter(Boolean),
  }
}

/** The recentFiles store patch that reopens the page a snapshot describes (`open: true` included). */
export function restoredPage(snapshot) {
  const { scope, query, applied, filter, view, selected } = recentPageSnapshot(snapshot)
  return { open: true, scope, query, applied, filter, view, selected }
}

export default recentFileCards
